/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// useGuidedChecklist
//
// Loads task-checklists + task-templates for the guided popup experience.
// Returns flat groups (today / later / done) + the "Up Next" item selection.
//
// Visibility gate mirrors AttendedChecklistV2 — feature flag is checked before
// any API call so practices without tasks-v2 never see 403 noise in Sentry.
//
// Data sources:
//   1. /api/v1/practices/{p}/leads/{l}/task-checklists/  → spawned tasks
//   2. /api/v1/practices/{p}/task-templates/?limit=200   → template metadata
//
// Applies-to filter: templates whose `applies_to_statuses` includes
// CONSULTATION_ATTENDED AND that have at least one checklist item on a task.
//
// Grouping (per design spec §5):
//   today  – incomplete items whose task.due_at ≤ end-of-today (incl. overdue)
//   later  – incomplete items with no due_at or due_at > end-of-today
//   done   – items where completed_at is non-null or status === 'completed'
//
// Up Next selection rule (design spec §5):
//   1. Earliest overdue item (task.due_at < start-of-today)
//   2. Else earliest due-today item
//   3. Else first template-ordered incomplete item
//
// DEDUPLICATION (fix for double useGuidedChecklist instantiation):
// AttendedAppointment / ConsultationAttendedAppointment call this hook to gate
// v2GuidedActive, and AttendedGuidedChecklistV2 calls it again with the same
// (practiceId, leadId).  Two independent hook instances reacting to each
// other's state updates caused cascading re-renders (stack overflow on rapid
// open/close).  Fix: a module-level store keyed by "practiceId:leadId" holds
// the single shared result + subscriber list.  All instances for the same key
// share one fetch and one batch of state, so there is no cross-instance cascade.
// The store entry is torn down when the last subscriber unmounts (refCount → 0).
// -----------------------------------------------------------------------------
import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import { filterGroupsByTreatment } from 'src/utils/checklistViewModel';

const ATTENDED_STATUS = 'CONSULTATION_ATTENDED';

// ---------------------------------------------------------------------------
// Module-level shared store — one entry per (practiceId:leadId) pair
// ---------------------------------------------------------------------------

/**
 * Default result snapshot used before the first fetch completes and shared
 * across all hook instances as the initial state.
 */
const INITIAL_SNAPSHOT = {
    loading: true,
    initialLoading: true,
    templatesEmpty: false,
    fetchError: false,
    groups: { today: [], later: [], done: [] },
    upNext: null,
    templatePreviewItems: [],
    allDoneCount: 0,
};

/**
 * Map<string, StoreEntry> — keyed by `${practiceId}:${leadId}`.
 * StoreEntry: {
 *   snapshot:    object     — latest result snapshot (same shape as INITIAL_SNAPSHOT)
 *   listeners:   Set<fn>    — subscriber callbacks; called whenever snapshot changes
 *   refCount:    number     — mounted subscribers; entry purged when it reaches 0
 *   hasDataRef:  { current: boolean } — shared latch: true once first fetch succeeds
 *   fetchAll:    function   — fetch + notify all listeners
 * }
 */
const _store = new Map();

function _getOrCreateEntry(practiceId, leadId) {
    const key = `${practiceId}:${leadId}`;
    if (_store.has(key)) return _store.get(key);

    const entry = {
        snapshot: { ...INITIAL_SNAPSHOT },
        listeners: new Set(),
        refCount: 0,
        hasDataRef: { current: false },
        fetchAll: null, // assigned below — needs closure over entry
    };

    /**
     * Merge a partial update into entry.snapshot and notify all listeners.
     */
    function _notify(patch) {
        entry.snapshot = { ...entry.snapshot, ...patch };
        // Re-derive initialLoading from patched loading + hasDataRef.
        // Must happen AFTER hasDataRef.current is latched.
        entry.snapshot.initialLoading = entry.snapshot.loading && !entry.hasDataRef.current;
        entry.listeners.forEach((cb) => cb(entry.snapshot));
    }

    async function fetchAll({ bustCache = false } = {}) {
        if (!practiceId || !leadId) {
            _notify({ loading: false, templatesEmpty: true });
            return;
        }
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            _notify({ loading: false, templatesEmpty: true });
            return;
        }

        _notify({ loading: true });
        try {
            const [checklistsRes, tplRes] = await Promise.all([
                callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`,
                    'GET', null, false,
                    bustCache ? { bustCache: true } : {}
                ),
                callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
            ]);

            _notify({ fetchError: false });

            let groupsData = [];
            if (Array.isArray(checklistsRes?.data)) groupsData = checklistsRes.data;
            else if (Array.isArray(checklistsRes?.data?.results)) groupsData = checklistsRes.data.results;

            let tplData = [];
            if (Array.isArray(tplRes?.data?.results)) tplData = tplRes.data.results;
            else if (Array.isArray(tplRes?.data)) tplData = tplRes.data;

            const tplStatusMap = new Map();
            tplData.forEach((t) => {
                tplStatusMap.set(
                    String(t.id),
                    Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []
                );
            });

            // Resolve ALL of the lead's treatments and build per-treatment scopes.
            // Previously this collapsed to a single matchedTreatment (find → [0]
            // fallback), which caused multi-treatment leads to silently drop every
            // treatment after the first ATTENDED one.
            //
            // Multi-treatment union strategy:
            //   1. Build a scope { leadTreatmentId, matchedLeadTreatmentId, label }
            //      for every treatment (no status filter — the template's
            //      applies_to_statuses gate handles relevance; the fallback [0] below
            //      preserves the old behaviour when none match the preferred statuses).
            //   2. For each scope, run filterGroupsByTreatment (which prevents
            //      cross-treatment task bleed) and union the results.
            //   3. When >1 treatments are present, stamp each group with
            //      _treatmentLabel so renderers can distinguish them.  When exactly
            //      one treatment is present, _treatmentLabel is omitted — byte-
            //      identical to the pre-fix behaviour for the common case.
            const allTreatments = (entry._latestLeadDetail?.lead_treatments) || [];

            // Fall back to the same priority order as the old single-treatment path
            // so that a lead with no recognised status still works.
            const fallbackTreatment =
                allTreatments.find((t) => t.status === ATTENDED_STATUS) ||
                allTreatments.find((t) => t.status === 'CONSULTATION_BOOKED') ||
                allTreatments[0];

            // Build one scope per treatment.  If there are no treatments at all,
            // produce a single null-scope that mirrors the old single-treatment path.
            const treatmentScopes = allTreatments.length > 0
                ? allTreatments.map((tx) => ({
                    treatment: tx,
                    leadTreatmentId: tx.treatment_data?.treatment_item?.id ?? null,
                    matchedLeadTreatmentId: tx.id ?? null,
                    label: tx.treatment_data?.name || tx.treatment_data?.treatment_item?.name || null,
                }))
                : [{
                    treatment: null,
                    leadTreatmentId: fallbackTreatment?.treatment_data?.treatment_item?.id ?? null,
                    matchedLeadTreatmentId: fallbackTreatment?.id ?? null,
                    label: null,
                }];

            // Sentry warning: replicate the old single-treatment warning for any
            // treatment that is missing its treatment_item id.
            treatmentScopes.forEach(({ treatment, leadTreatmentId: ltId }) => {
                if (treatment && ltId == null) {
                    Sentry.captureMessage('useGuidedChecklist: leadTreatmentItemId missing on lead_treatment', {
                        level: 'warning',
                        tags: { context: 'guided_checklist_treatment_id' },
                        extra: { practiceId, leadId, treatmentDataKeys: Object.keys(treatment?.treatment_data || {}) },
                    });
                }
            });

            const tplTreatmentMap = new Map();
            tplData.forEach((t) => {
                tplTreatmentMap.set(
                    String(t.id),
                    Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []
                );
            });

            // passesTreatmentFilter scoped to a particular leadTreatmentId.
            const makePassesTreatmentFilter = (leadTreatmentId) => (t) => {
                const tItems = Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : [];
                if (tItems.length === 0) return true;
                if (leadTreatmentId == null) return false;
                return tItems.some((ti) => String(ti?.id) === String(leadTreatmentId));
            };

            // applicableTemplatesExist: true when ANY scope has a matching template.
            const applicableTemplatesExist = treatmentScopes.some(({ leadTreatmentId }) => {
                const passesTreatmentFilter = makePassesTreatmentFilter(leadTreatmentId);
                return tplData.some((t) => {
                    const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
                    const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
                    const isActive = t.is_active !== false;
                    return statuses.includes(ATTENDED_STATUS) && items.length > 0 && isActive && passesTreatmentFilter(t);
                });
            });

            if (!applicableTemplatesExist) {
                _notify({
                    templatesEmpty: true,
                    groups: { today: [], later: [], done: [] },
                    upNext: null,
                    templatePreviewItems: [],
                    allDoneCount: 0,
                });
                return;
            }

            // Union filtered groups across all treatment scopes.
            // Each scope runs filterGroupsByTreatment independently so task-level
            // cross-treatment bleed is prevented per group.
            // When >1 treatment exists, stamp _treatmentLabel on each group; when
            // exactly 1, omit it to keep rendering byte-identical.
            const multiTx = treatmentScopes.length > 1;
            const filteredGroupsData = treatmentScopes.flatMap(({ leadTreatmentId, matchedLeadTreatmentId, label }) => {
                const scopeGroups = filterGroupsByTreatment(groupsData, {
                    tplStatusMap,
                    tplTreatmentMap,
                    leadTreatmentId,
                    matchedLeadTreatmentId,
                });
                if (!multiTx || !label) return scopeGroups;
                // I-1 fix (parity with AttendedChecklistV2 L148-149 and useCompletionChecklist):
                // generic attended templates (no applies_to_treatment_items) pass every
                // scope's filter and must NOT inherit the first scope's treatment name —
                // stamping them would show "Invisalign" on a group that applies to ALL
                // treatments, misleading reps on multi-treatment leads. Only treatment-
                // specific groups (tplTreatmentMap has at least one item for this template)
                // get the label; generic groups keep _treatmentLabel: undefined.
                return scopeGroups.map((g) => {
                    const isTreatmentSpecific = (tplTreatmentMap.get(String(g.template_id)) || []).length > 0;
                    return { ...g, _treatmentLabel: isTreatmentSpecific ? label : undefined };
                });
            });

            // De-duplicate groups that appear in multiple scopes (same template_id +
            // same set of task ids) — e.g. a generic template whose tasks have no
            // lead_treatment and therefore pass every scope's filter.
            const seenGroupKeys = new Set();
            const dedupedGroupsData = filteredGroupsData.filter((g) => {
                const taskIds = (g.tasks || []).map((t) => t.id).sort().join(',');
                const key = `${g.template_id}::${taskIds}`;
                if (seenGroupKeys.has(key)) return false;
                seenGroupKeys.add(key);
                return true;
            });

            const flatItems = buildFlatItems(dedupedGroupsData, tplStatusMap);

            if (flatItems.length === 0) {
                let progressTotal = 0;
                let progressCompleted = 0;
                dedupedGroupsData.forEach((group) => {
                    const statuses = tplStatusMap.get(String(group.template_id)) || [];
                    if (!statuses.includes(ATTENDED_STATUS)) return;
                    const p = group.progress || {};
                    progressTotal += (p.total || 0);
                    progressCompleted += (p.completed || 0);
                });
                // Guard: only declare "all follow-ups complete" when we have a positive
                // progress total AND the deduped groups we summed actually cover at least
                // one scope from treatmentScopes.  If a treatment is archived/detached
                // mid-session, filterGroupsByTreatment may return fewer groups than there
                // are active scopes, making dedupedGroupsData a partial picture.
                //
                // Minimum safety check: dedupedGroupsData must be non-empty (if it is
                // empty, progressTotal is 0 which already fails the check below, but the
                // explicit guard makes the intent clear for future readers).  We cannot
                // exhaustively verify every scope was covered without a round-trip, so we
                // rely on the existing progressTotal > 0 gate as the primary defence;
                // this comment documents the known limitation so any future tightening of
                // the guard (e.g. counting covered scopes) has the right context.
                const hasCoveredGroups = dedupedGroupsData.length > 0;
                if (hasCoveredGroups && progressTotal > 0 && progressCompleted >= progressTotal) {
                    _notify({
                        allDoneCount: progressTotal,
                        groups: { today: [], later: [], done: [] },
                        upNext: null,
                        templatePreviewItems: [],
                        templatesEmpty: false,
                    });
                    return;
                }
            }

            // templatePreviewItems: built from all applicable scopes when no tasks
            // have been spawned yet.  Reuse the first scope's passesTreatmentFilter
            // that produced applicable templates — takes the union across all scopes
            // by checking if ANY scope's filter would pass the template.
            const nextTemplatePreviewItems = [];
            if (flatItems.length === 0) {
                const applicable = tplData
                    .filter((t) => {
                        const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
                        const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
                        if (!statuses.includes(ATTENDED_STATUS) || items.length === 0 || t.is_active === false) return false;
                        return treatmentScopes.some(({ leadTreatmentId }) =>
                            makePassesTreatmentFilter(leadTreatmentId)(t)
                        );
                    })
                    .sort((a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity));
                applicable.forEach((tpl) => {
                    const sortedCi = [...(tpl.checklist_items || [])].sort(
                        (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity)
                    );
                    sortedCi.forEach((ci, idx) => {
                        nextTemplatePreviewItems.push({
                            id: `preview-${tpl.id}-${idx}`,
                            title: ci.title || ci.name || '',
                            description: ci.description || '',
                            item_type: ci.item_type || ci.action_type || '',
                            due_at: null,
                            completed_at: null,
                            _preview: true,
                            _templateId: tpl.id,
                            _templateName: tpl.name || '',
                        });
                    });
                });
            }

            const builtGroups = buildGroups(flatItems);
            const nextItem = selectUpNext(flatItems);

            _notify({
                templatesEmpty: false,
                allDoneCount: 0,
                groups: builtGroups,
                upNext: nextItem,
                templatePreviewItems: nextTemplatePreviewItems,
            });
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'guided_checklist_fetch' } });
            _notify({ fetchError: true });
            if (!entry.hasDataRef.current) {
                _notify({
                    templatesEmpty: true,
                    groups: { today: [], later: [], done: [] },
                    upNext: null,
                    templatePreviewItems: [],
                    allDoneCount: 0,
                });
            }
        } finally {
            entry.hasDataRef.current = true;
            _notify({ loading: false });
        }
    }

    entry.fetchAll = fetchAll;
    // Storage for the most recently seen leadDetail (set by each subscriber on each render).
    entry._latestLeadDetail = null;
    _store.set(key, entry);
    return entry;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Midnight at the start of today (local). */
function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

/** One millisecond before midnight at the end of today (local). */
function endOfToday() {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
}

/** Returns true when the checklist item is completed, skipped, or paused — all are out of the active queue. */
function isItemCompleted(item) {
    if (!item) return false;
    if (item.completed_at != null) return true;
    if (item.status === 'completed') return true;
    // SKIPPED items have completion_state set but completed_at may be null and the
    // task-level status stays 'pending' — they still need to drop out of active.
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.SKIPPED) return true;
    // PAUSED items must also stay out of the active pool after a cache-busted refetch.
    // Without this guard a paused item re-enters the queue as soon as the GET returns.
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED) return true;
    // NOTE: do NOT check item.paused_until here — the /pause/ endpoint sets
    // paused_until on ALL items in the task group, not just the targeted item.
    // That caused every item to be treated as done → false all-done state.
    // Paused-item display is handled via optimisticPausedItems in the orchestrator.
    return false;
}

// ---------------------------------------------------------------------------
// Treatment filter helper — re-exported from checklistViewModel for back-compat.
// Other files (AttendedChecklistV2, useCompletionChecklist, etc.) import
// filterGroupsByTreatment from this hook; the named re-export preserves that.
// ---------------------------------------------------------------------------
export { filterGroupsByTreatment };

// ---------------------------------------------------------------------------
// Flat-item builder
// ---------------------------------------------------------------------------

/**
 * Converts the nested groups/tasks/items structure into a flat array of items
 * augmented with their parent task's due_at and template metadata.
 *
 * Ordering preserved from API response so that "first template-ordered item"
 * (upNext rule #3) is simply flatItems.find(i => !i.done).
 */
function buildFlatItems(groups, tplStatusMap) {
    const items = [];
    let globalOrder = 0;

    groups.forEach((group) => {
        const statuses = tplStatusMap.get(String(group.template_id)) || [];
        if (!statuses.includes(ATTENDED_STATUS)) return;

        const tasks = group.tasks || [];
        tasks.forEach((task) => {
            const checklistItems = task.checklist_items || [];
            if (checklistItems.length === 0) return;

            // When a task is completed (e.g. via the cascade fired by marking it
            // complete from the lead-detail view) the parent task.status flips to
            // 'completed' but the child checklist items can keep a STALE
            // completion_state ('PENDING') and a null completed_at — the BE cascade
            // updates the task row, not every item row.  Without this guard those
            // items fail isItemCompleted() and leak back into the popup's
            // today/later/Up-Next lists, so a completed task shows as still open.
            // Treat every item of a completed task as done.
            const taskCompleted = String(task.status || '').toLowerCase() === 'completed';

            // Sort checklist items by sort_order (ascending; nulls last)
            const sortedItems = [...checklistItems].sort(
                (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity)
            );

            sortedItems.forEach((item) => {
                // #21 — skip structural / parent trigger items that carry no
                // actionable type or display title; they would render as blank cards.
                const resolvedType = item.item_type || item.action_type || '';
                const resolvedTitle = item.title || item.name || '';
                if (!resolvedType && !resolvedTitle) return;

                items.push({
                    // stable identity
                    itemId: item.id ?? item.checklist_item_id,
                    id: item.id ?? item.checklist_item_id,
                    taskId: task.id,
                    taskTitle: task.title,
                    templateId: group.template_id,
                    templateName: group.template_name,
                    // Present only when the lead has multiple treatments — allows
                    // renderers to optionally prefix the group header with the
                    // treatment name.  Omitted (undefined) for single-treatment leads
                    // so existing render paths are byte-identical.
                    _treatmentLabel: group._treatmentLabel ?? undefined,
                    // Source TaskTemplateChecklistItem id — the ETM resolve key the
                    // sidebar email composer uses to look up the per-practice template.
                    template_item: item.template_item ?? item.template_item_id ?? null,
                    // checklist item fields promoted to top level for component access
                    item_type: item.item_type || item.action_type || '',
                    title: item.title || item.name || '',
                    description: item.description || '',
                    completed_at: item.completed_at || null,
                    status: item.status || null,
                    sort_order: item.sort_order ?? null,
                    // due_at lives on the task, not the item
                    due_at: task.due_at || null,
                    // completion state — promoted so DoneCard chip resolves YES/SKIPPED/PAUSED.
                    // is_completed and paused_until are also forwarded so the chip can show
                    // the correct label and the paused-until date without digging into raw.
                    completion_state: item.completion_state || null,
                    is_completed:     item.is_completed ?? null,
                    paused_until:     item.paused_until  || null,
                    done: isItemCompleted(item) || taskCompleted,
                    // template-ordering for upNext rule #3
                    templateOrder: globalOrder,
                    // pass raw objects through for downstream consumers
                    raw: item,
                    rawTask: task,
                });
                globalOrder += 1;
            });
        });
    });

    return items;
}

// ---------------------------------------------------------------------------
// Group builder
// ---------------------------------------------------------------------------

function buildGroups(flatItems) {
    const todayEnd = endOfToday();

    const today = [];
    const later = [];
    const done = [];

    flatItems.forEach((item) => {
        if (item.done) {
            done.push(item);
            return;
        }
        if (!item.due_at) {
            later.push(item);
            return;
        }
        const d = parseApiDate(item.due_at)?.toDate();   // API date — COMPARE
        if (d && d <= todayEnd) {
            // Covers overdue (d < todayStart) and today (d <= todayEnd)
            today.push(item);
        } else {
            later.push(item);
        }
    });

    // Sort today group: overdue first (ascending due_at), then within-today
    today.sort((a, b) => (parseApiDate(a.due_at)?.valueOf() ?? Infinity) - (parseApiDate(b.due_at)?.valueOf() ?? Infinity));

    return { today, later, done };
}

// ---------------------------------------------------------------------------
// Up Next selector
// ---------------------------------------------------------------------------

function selectUpNext(flatItems) {
    const todayStart = startOfToday();
    const todayEnd = endOfToday();
    const incomplete = flatItems.filter((i) => !i.done);
    if (incomplete.length === 0) return null;

    // Rule 1: earliest overdue
    const overdue = incomplete
        .filter((i) => {
            if (!i.due_at) return false;
            const d = parseApiDate(i.due_at)?.toDate();   // API date — COMPARE
            return d && d < todayStart;
        })
        .sort((a, b) => (parseApiDate(a.due_at)?.valueOf() ?? Infinity) - (parseApiDate(b.due_at)?.valueOf() ?? Infinity));
    if (overdue.length > 0) return overdue[0];

    // Rule 2: earliest due today
    const dueToday = incomplete
        .filter((i) => {
            if (!i.due_at) return false;
            const d = parseApiDate(i.due_at)?.toDate();   // API date — COMPARE
            return d && d >= todayStart && d <= todayEnd;
        })
        .sort((a, b) => (parseApiDate(a.due_at)?.valueOf() ?? Infinity) - (parseApiDate(b.due_at)?.valueOf() ?? Infinity));
    if (dueToday.length > 0) return dueToday[0];

    // Rule 3: first template-ordered incomplete item
    return incomplete.sort((a, b) => a.templateOrder - b.templateOrder)[0];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {object} params
 * @param {string|number} params.practiceId
 * @param {string|number} params.leadId
 * @param {object}        params.leadDetail  – lead object from Redux
 *
 * @returns {{
 *   loading: boolean,
 *   initialLoading: boolean,
 *   templatesEmpty: boolean,
 *   fetchError: boolean,
 *   groups: { today: object[], later: object[], done: object[] },
 *   upNext: object|null,
 *   templatePreviewItems: object[],
 *   allDoneCount: number,
 *   refresh: function,
 * }}
 */
export default function useGuidedChecklist({ practiceId, leadId, leadDetail }) {
    // Re-fetch when a template mutation lands (same pattern as AttendedChecklistV2)
    const templatesUpdatedAt = useSelector((s) => s?.taskTemplate?.templatesUpdatedAt || 0);

    // Serialised signature of the lead's treatments (id + status + treatment_item).
    // Drives the fetch dep array AND lets the trigger effect detect a STATUS change
    // (e.g. after RecordAttended) so it can bust the GET dedupe cache for fresh data.
    const treatmentSig = JSON.stringify(
        (leadDetail?.lead_treatments || []).map((t) => ({ id: t.id, s: t.status, ti: t.treatment_data?.treatment_item?.id ?? null }))
    );

    // Get or create the shared entry for this (practiceId, leadId) pair.
    // This is stable across re-renders for the same key — _store is a module-level Map.
    const entry = _getOrCreateEntry(practiceId, leadId);

    // Always write the latest leadDetail so fetchAll uses up-to-date treatment data.
    entry._latestLeadDetail = leadDetail;

    // Local snapshot state — updated whenever the shared entry notifies subscribers.
    const [snapshot, setSnapshot] = useState(() => entry.snapshot);

    // Stable wrapped refresh exposed to callers — calls into the shared fetchAll.
    const refresh = useCallback((opts) => entry.fetchAll(opts), [entry]);

    // Subscribe / unsubscribe on mount / unmount.
    useEffect(() => {
        entry.refCount += 1;

        const listener = (nextSnapshot) => {
            setSnapshot({ ...nextSnapshot });
        };
        entry.listeners.add(listener);

        // Sync immediately in case snapshot changed between last render and this effect.
        setSnapshot({ ...entry.snapshot });

        return () => {
            entry.listeners.delete(listener);
            entry.refCount -= 1;
            if (entry.refCount <= 0) {
                // Last subscriber unmounted — purge the entry so the next popup open
                // starts fresh (correct for rapid close/reopen with a different lead).
                const key = `${practiceId}:${leadId}`;
                _store.delete(key);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, leadId]);

    // Trigger fetch whenever (practiceId, leadId, treatmentSig, templatesUpdatedAt)
    // changes.  Always bust the GET dedupe cache — see the original comment above.
    useEffect(() => {
        entry.fetchAll({ bustCache: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry, treatmentSig, templatesUpdatedAt]);

    return {
        loading: snapshot.loading,
        initialLoading: snapshot.initialLoading,
        templatesEmpty: snapshot.templatesEmpty,
        fetchError: snapshot.fetchError,
        groups: snapshot.groups,
        upNext: snapshot.upNext,
        templatePreviewItems: snapshot.templatePreviewItems,
        allDoneCount: snapshot.allDoneCount,
        refresh,
    };
}
